import morphdom from "morphdom";
import { NumberInput } from "./g-number-input";

class CartSection extends HTMLElement {
  constructor() {
    super();

    window.jollyVariables.cartSectionId = this.dataset.sectionId;
    this.emptyEl = this.querySelector(".js-empty");
    this.nonemptyEl = this.querySelector(".js-nonempty");
    this.recapEl = this.nonemptyEl.querySelector(".js-recap");
    this.lineItemsEl = this.nonemptyEl.querySelector(".js-line-items");
    this.intelligentBooksellerEl = this.nonemptyEl.querySelector(".js-intelligent-bookseller");
    this.intelligentBooksellerModalButtonOnlyEl = this.nonemptyEl.querySelector(".js-intelligent-bookseller-modal-button-only");
    this.scrollContainer = window.location.pathname === "/cart" ? window : this.closest(".js-modal-content-wrapper");
    this.modal = this.closest("g-modal");
    this.recommendationsEl = this.querySelector("cart-recommendations");
    
    // Initialize recommendations order control
    this._setupRecommendationsOrderControl();
  }

  connectedCallback() {
    this._attachEventListeners();
  }

  _setupRecommendationsOrderControl() {
    if (!this.recommendationsEl) return;

    // Store reference to this instance
    const cartInstance = this;
    
    // Store the current value if it exists
    let currentValue = window.cartRecommendationsOrderTest;
    
    // Apply initial order
    this._updateRecommendationsOrder(currentValue);
    
    // Create setter to watch for changes
    Object.defineProperty(window, 'cartRecommendationsOrderTest', {
      get() {
        return currentValue;
      },
      set(value) {
        currentValue = value;
        cartInstance._updateRecommendationsOrder(value);
      },
      configurable: true
    });
  }

  _updateRecommendationsOrder(testValue) {
    if (!this.recommendationsEl) return;
    
    if (testValue === true) {
      this.recommendationsEl.style.order = "-1";
    } else {
      this.recommendationsEl.style.order = "";
    }
  }

  _attachEventListeners() {
    document.documentElement.addEventListener("cart:rerender", this._rerender.bind(this));
  }

  _rerender(e) {
    const { sectionEl, cart, scrollToTop } = e.detail;

    if (cart.item_count === 0) {
      this.emptyEl.setAttribute("aria-hidden", false);
      this.nonemptyEl.setAttribute("aria-hidden", true);
    } else {
      this.emptyEl.setAttribute("aria-hidden", true);
      this.nonemptyEl.setAttribute("aria-hidden", false);
      const newLineItemsEl = sectionEl.querySelector(".js-line-items");
      const newRecapEl = sectionEl.querySelector(".js-recap");
      const newIntelligentBooksellerEl = sectionEl.querySelector(".js-intelligent-bookseller");
      const newIntelligentBooksellerModalButtonOnlyEl = sectionEl.querySelector(".js-intelligent-bookseller-modal-button-only");
      morphdom(this.lineItemsEl, newLineItemsEl);
      morphdom(this.recapEl, newRecapEl);
      if (this.intelligentBooksellerEl && newIntelligentBooksellerEl) {
        morphdom(this.intelligentBooksellerEl, newIntelligentBooksellerEl);
      }
      if (this.intelligentBooksellerModalButtonOnlyEl && newIntelligentBooksellerModalButtonOnlyEl) {
        morphdom(this.intelligentBooksellerModalButtonOnlyEl, newIntelligentBooksellerModalButtonOnlyEl);
      }
    }

    if (scrollToTop) {
      this.scrollContainer.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }

    if(this.modal && !this.modal.expanded) {
      this.modal.open();
    }
  }
}

class CartItemQuantityInput extends NumberInput {
  constructor() {
    super();
    this._abortController = null;
  }

  _onValueChange(e) {
    super._onValueChange(e);
    this._updateQuantity();
  }

  async _updateQuantity() {
    const updates = {};
    updates[this.dataset.lineItemKey] = this.value;

    if (this._abortController) {
      this._abortController.abort();
    }

    this._abortController = new AbortController();

    try {
      document.dispatchEvent(new CustomEvent("theme:loading:start"));

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ updates, sections: window.jollyVariables.cartSectionId }),
        signal: this._abortController.signal,
      });

      if (response.ok) {
        const json = await response.json();
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = json.sections[window.jollyVariables.cartSectionId];

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:rerender", {
            detail: {
              sectionEl: tempDiv.children[0],
              cart: { item_count: json.item_count, items: json.items },
              scrollToTop: false,
            },
          })
        );
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Previous request aborted");
      } else {
        console.error(error);
      }
    } finally {
      document.dispatchEvent(new CustomEvent("theme:loading:end"));
    }
  }
}

class CartItemRemoveBtn extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", this.removeItem.bind(this));
  }

  async removeItem() {
    const updates = {};
    updates[this.dataset.lineItemKey] = 0;

    try {
      document.dispatchEvent(new CustomEvent("theme:loading:start"));

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ updates, sections: window.jollyVariables.cartSectionId }),
      });

      if (response.ok) {
        const json = await response.json();
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = json.sections[window.jollyVariables.cartSectionId];

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:rerender", {
            detail: {
              sectionEl: tempDiv.children[0],
              cart: { item_count: json.item_count, items: json.items },
              scrollToTop: false,
            },
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      document.dispatchEvent(new CustomEvent("theme:loading:end"));
    }
  }
}

if (!customElements.get("cart-section")) {
  customElements.define("cart-section", CartSection);
}
if (!customElements.get("cart-item-quantity-input")) {
  customElements.define("cart-item-quantity-input", CartItemQuantityInput);
}
if (!customElements.get("cart-item-remove-btn")) {
  customElements.define("cart-item-remove-btn", CartItemRemoveBtn);
}
